import { useCallback, useEffect, useRef, useState, type RefObject } from "react";

export interface WallMode {
  /** True only while THIS container is the fullscreened element — derived
   *  from `document.fullscreenElement`, never set optimistically on click.
   *  F11, Escape, and the OS window manager can all exit fullscreen without
   *  ever calling `exit()`, so anything that set this eagerly would desync. */
  isActive: boolean;
  isSupported: boolean;
  containerRef: RefObject<HTMLDivElement | null>;
  enter: () => Promise<void>;
  exit: () => Promise<void>;
  toggle: () => void;
  error: string | null;
}

// Wraps the native Fullscreen API for the live wall's "wall mode" — pure
// presentation, no streaming/lease concerns: fullscreening a container the
// grid already lives in hides the surrounding TopNav/SideNav chrome for
// free (that's what the Fullscreen API does), without this needing to touch
// any stream state.
export function useWallMode(): WallMode {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isActive, setIsActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isSupported = typeof document !== "undefined" && document.fullscreenEnabled;

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsActive(document.fullscreenElement === containerRef.current);
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFullscreenChange);
      // Unmount guard: navigating away from Live while wall mode is active
      // must not leave the browser fullscreened on a now-detached node.
      // exitFullscreen()'s promise can reject (observed in practice: "Document
      // not active" when the document itself is mid-teardown) — this runs
      // during cleanup with nothing left to report an error to, so the only
      // correct thing to do with a rejection here is swallow it, not leave it
      // unhandled.
      if (document.fullscreenElement === containerRef.current) {
        document.exitFullscreen().catch(() => {});
      }
    };
  }, []);

  const enter = useCallback(async () => {
    setError(null);
    try {
      await containerRef.current?.requestFullscreen();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't enter fullscreen");
    }
  }, []);

  const exit = useCallback(async () => {
    setError(null);
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't exit fullscreen");
    }
  }, []);

  const toggle = useCallback(() => {
    if (isActive) void exit();
    else void enter();
  }, [isActive, enter, exit]);

  return { isActive, isSupported, containerRef, enter, exit, toggle, error };
}
